/* eslint-disable max-lines-per-function --
   TODO: 招募前 sprint 拆組件 (TODO-frontend-eslint-ci-warnings-as-errors)。
   2026-02-06 規則加入時 87 個既有違規凍結為 baseline，新檔/新函數仍受 hook 攔截。
*/
import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import authService from '../../services/authService';

/**
 * 修改密碼對話框元件
 *
 * @param {boolean} open - 對話框是否開啟
 * @param {Function} onClose - 關閉對話框的回調函數
 */
const ChangePasswordDialog = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // 計算密碼強度
  const calculatePasswordStrength = (password) => {
    if (!password) return 0;

    let strength = 0;
    if (password.length >= 8) strength += 20;
    if (password.length >= 12) strength += 20;
    if (/[a-z]/.test(password)) strength += 20;
    if (/[A-Z]/.test(password)) strength += 20;
    if (/[0-9]/.test(password)) strength += 20;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 20;

    return Math.min(100, strength);
  };

  const passwordStrength = calculatePasswordStrength(formData.newPassword);

  // 獲取密碼強度的顏色
  const getStrengthColor = () => {
    if (passwordStrength < 40) return 'error';
    if (passwordStrength < 70) return 'warning';
    return 'success';
  };

  // 獲取密碼強度的文字
  const getStrengthText = () => {
    if (passwordStrength < 40) return '弱';
    if (passwordStrength < 70) return '中';
    return '強';
  };

  // 處理輸入變更
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  // 切換密碼顯示/隱藏
  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // 驗證表單
  const validateForm = () => {
    if (!formData.oldPassword) {
      setError('請輸入舊密碼');
      return false;
    }
    if (!formData.newPassword) {
      setError('請輸入新密碼');
      return false;
    }
    if (formData.newPassword.length < 8) {
      setError('新密碼長度至少要 8 個字元');
      return false;
    }
    if (formData.newPassword === formData.oldPassword) {
      setError('新密碼不能與舊密碼相同');
      return false;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError('新密碼與確認密碼不一致');
      return false;
    }
    return true;
  };

  // 處理提交
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const response = await authService.changePassword(formData.oldPassword, formData.newPassword);

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          handleClose();
          // 可選：重新導向到登入頁
          // window.location.href = '/login';
        }, 2000);
      } else {
        setError(response.message || '修改密碼失敗');
      }
    } catch (err) {
      console.error('修改密碼錯誤:', err);
      setError(err.response?.data?.message || '修改密碼失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  // 關閉對話框並重置狀態
  const handleClose = () => {
    setFormData({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setShowPassword({
      old: false,
      new: false,
      confirm: false,
    });
    setError('');
    setSuccess(false);
    setLoading(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="div">
            修改密碼
          </Typography>
          <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            密碼修改成功！請使用新密碼重新登入。
          </Alert>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="舊密碼"
              name="oldPassword"
              type={showPassword.old ? 'text' : 'password'}
              value={formData.oldPassword}
              onChange={handleChange}
              margin="normal"
              required
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => togglePasswordVisibility('old')} edge="end">
                      {showPassword.old ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="新密碼"
              name="newPassword"
              type={showPassword.new ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={handleChange}
              margin="normal"
              required
              disabled={loading}
              helperText="密碼長度至少 8 個字元"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => togglePasswordVisibility('new')} edge="end">
                      {showPassword.new ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {formData.newPassword && (
              <Box sx={{ mt: 1, mb: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    密碼強度
                  </Typography>
                  <Typography
                    variant="caption"
                    color={getStrengthColor() + '.main'}
                    fontWeight="bold"
                  >
                    {getStrengthText()}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength}
                  color={getStrengthColor()}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            )}

            <TextField
              fullWidth
              label="確認新密碼"
              name="confirmPassword"
              type={showPassword.confirm ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              margin="normal"
              required
              disabled={loading}
              error={formData.confirmPassword && formData.newPassword !== formData.confirmPassword}
              helperText={
                formData.confirmPassword && formData.newPassword !== formData.confirmPassword
                  ? '密碼不一致'
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => togglePasswordVisibility('confirm')} edge="end">
                      {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                密碼安全提示：
              </Typography>
              <Typography variant="caption" component="ul" sx={{ pl: 2, color: 'text.secondary' }}>
                <li>使用至少 8 個字元</li>
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          取消
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || success}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? '處理中...' : '確認修改'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;
